import axios from 'axios';
import type {
  Media,
  Playlist,
  PlaylistItem,
  ScheduleEvent,
  WebPlayerBootstrap,
  WebPlayerCommand,
} from '../types';
import { environmentConfig } from '../config/environment';

const baseURL = `${environmentConfig.apiBaseUrl}/api/v1`;

class PlayerService {
  private client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private path(token: string, suffix: string) {
    return `/web-player/${encodeURIComponent(token)}${suffix}`;
  }

  async bootstrap(token: string): Promise<WebPlayerBootstrap> {
    const response = await this.client.get(this.path(token, '/bootstrap'));
    return response.data;
  }

  async heartbeat(token: string, data: Record<string, any>) {
    const response = await this.client.post(this.path(token, '/heartbeat'), data);
    return response.data;
  }

  async syncStatus(token: string, sessionId: number, data: Record<string, any>) {
    const response = await this.client.post(this.path(token, `/sync/${sessionId}/status`), data);
    return response.data;
  }

  async pendingCommands(token: string): Promise<{ commands: WebPlayerCommand[] }> {
    const response = await this.client.get(this.path(token, '/commands/pending'));
    return response.data;
  }

  async sendCommandResult(token: string, commandId: number, data: Record<string, any>) {
    const response = await this.client.post(this.path(token, `/commands/${commandId}/result`), data);
    return response.data;
  }

  async scheduleEvents(token: string): Promise<ScheduleEvent[]> {
    const response = await this.client.get(this.path(token, '/schedule/events'));
    return response.data;
  }

  async assignedMedia(token: string): Promise<{ items: Media[] }> {
    const response = await this.client.get(this.path(token, '/assigned-media'));
    return response.data;
  }

  async assignedPlaylists(token: string): Promise<{ items: Playlist[] }> {
    const response = await this.client.get(this.path(token, '/assigned-playlists'));
    return response.data;
  }

  async playlistItems(token: string, playlistId: number): Promise<{ playlist_id: number; items: PlaylistItem[] }> {
    const response = await this.client.get(this.path(token, `/playlists/${playlistId}/items`));
    return response.data;
  }

  async mediaInfo(token: string, mediaId: number): Promise<Media> {
    const response = await this.client.get(this.path(token, `/media/${mediaId}/info`));
    return response.data;
  }

  mediaUrl(token: string, mediaId: number): string {
    return `${baseURL}${this.path(token, `/media/${mediaId}`)}`;
  }

  async proofStart(token: string, data: Record<string, any>): Promise<{ id: number; status: string }> {
    const response = await this.client.post(this.path(token, '/proof-of-play/start'), data);
    return response.data;
  }

  async proofFinish(token: string, proofId: number, data: Record<string, any>) {
    const response = await this.client.post(this.path(token, `/proof-of-play/${proofId}/finish`), data);
    return response.data;
  }
}

export const playerService = new PlayerService();
export default playerService;
